import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Loader2 } from "lucide-react";
import api from "../lib/api";
import BlogHeader from "../components/BlogHeader";
import { useSeo } from "../lib/seo";

export default function Blog() {
  useSeo({
    title: "Blog Lumora | Guide e Consigli su SEO, Web Design e Presenza Online",
    description:
      "Leggi gli articoli del blog Lumora: guide pratiche su SEO locale, manutenzione siti web, web design per piccole attività e strategie per crescere online.",
    path: "/blog",
  });
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    api
      .get("/blog")
      .then((r) => setPosts(r.data))
      .catch(() => setPosts([]));
  }, []);

  return (
    <div data-testid="blog-page" className="min-h-screen bg-[#FAF8F5]">
      <BlogHeader />
      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="mono-label text-[#E05638]">Lumora Journal</p>
          <h1 className="font-display font-extrabold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl mt-5">
            Idee, SEO e web design.
          </h1>
          <p className="mt-5 text-neutral-600 max-w-xl leading-relaxed">
            Guide pratiche per far crescere la tua presenza online, scritte da chi costruisce siti
            tutti i giorni.
          </p>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-2 gap-6">
          {!posts ? (
            <Loader2 className="animate-spin text-neutral-400" />
          ) : posts.length === 0 ? (
            <p className="text-sm text-neutral-500">Nessun articolo pubblicato ancora. Torna presto!</p>
          ) : (
            posts.map((p, i) => (
              <motion.article
                key={p.id}
                data-testid={`blog-card-${p.slug}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: (i % 2) * 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="group"
              >
                <Link to={`/blog/${p.slug}`} className="block">
                  <div className="overflow-hidden rounded-2xl border border-black/10 bg-white">
                    {p.cover ? (
                      <img
                        src={p.cover}
                        alt={p.title}
                        loading="lazy"
                        className="w-full h-56 object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-56 bg-[#1E293B] grid place-items-center transition-colors duration-500 group-hover:bg-[#E05638]">
                        <span className="font-display font-extrabold text-4xl text-white/20 group-hover:text-white/40 transition-colors">
                          L<span className="text-white/40">.</span>
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-start justify-between gap-4 mt-5 px-1">
                    <div>
                      <h2 className="font-display font-bold text-xl leading-snug group-hover:text-[#E05638] transition-colors">
                        {p.title}
                      </h2>
                      <p className="text-sm text-neutral-600 mt-2 leading-relaxed line-clamp-2">{p.excerpt}</p>
                      <p className="mono-label text-neutral-400 mt-3">
                        {p.created_at ? new Date(p.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : ""}
                      </p>
                    </div>
                    <span className="shrink-0 w-9 h-9 rounded-full border border-black/15 grid place-items-center text-neutral-500 group-hover:bg-[#E05638] group-hover:border-[#E05638] group-hover:text-white transition-all">
                      <ArrowUpRight size={16} />
                    </span>
                  </div>
                </Link>
              </motion.article>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
